// components/admin/accounting/PaymentsAccounting.jsx - WITH DRIVERS + REFERIDOS TAB + TOTALS
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Users, Package, Truck, RefreshCw, UserPlus, TrendingUp, DollarSign } from 'lucide-react';
import PaymentsTable from './PaymentsTable';

const API_URL = process.env.REACT_APP_API_URL;

function PaymentsAccounting({ userId, selectedDate }) {
  const [activeSubTab, setActiveSubTab] = useState('users');
  const [usersData, setUsersData] = useState([]);
  const [providersData, setProvidersData] = useState([]);
  const [driversData, setDriversData] = useState([]);
  const [referralsData, setReferralsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPaymentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  async function fetchPaymentsData() {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/accounting/payments`, {
        params: { admin_id: userId, date: selectedDate }
      });
      setUsersData(response.data.users || []);
      setProvidersData(response.data.providers || []);
      setDriversData(response.data.drivers || []);
      setReferralsData(response.data.referrals || []);
    } catch (error) {
      console.error('Error fetching payments data:', error);
      setError('Error al cargar los datos de pagos');
    } finally {
      setIsLoading(false);
    }
  }

  function handleRefresh() {
    fetchPaymentsData();
  }

  function patchPaymentRow(id, type, patch) {
    const updateRows = (rows) =>
      rows.map((item) => (item.id === id ? { ...item, ...patch } : item));

    const stateByType = {
      user: setUsersData,
      provider: setProvidersData,
      driver: setDriversData,
      referido: setReferralsData,
    };

    const setData = stateByType[type];
    if (setData) {
      setData(updateRows);
    }
  }

  async function handleUpdatePayment(id, field, value, type) {
    try {
      if (field === 'refresh') {
        await fetchPaymentsData();
        return;
      }

      if (field === 'images:replace') {
        patchPaymentRow(id, type, value);
        return;
      }

      const response = await axios.put(`${API_URL}/api/accounting/payments/${id}`, {
        admin_id: userId,
        field,
        value,
        type
      });
      patchPaymentRow(id, type, response.data.payment || {});
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Error al actualizar el pago');
    }
  }

  // ── Totals computed purely in UI ──────────────────────────────────────────
  const sumDebe = (arr) =>
    arr.reduce((acc, item) => acc + (parseFloat(item.debe) || 0), 0);

  const totals = useMemo(() => {
    const usersTotal     = sumDebe(usersData);
    const providersTotal = sumDebe(providersData);
    const combined       = usersTotal + providersTotal;

    const activeData = {
      users:     usersData,
      providers: providersData,
      drivers:   driversData,
      referrals: referralsData,
    }[activeSubTab] || [];

    return {
      users:     usersTotal,
      providers: providersTotal,
      combined,
      activeTab: sumDebe(activeData),
    };
  }, [usersData, providersData, driversData, referralsData, activeSubTab]);

  const formatSoles = (amount) =>
    `S/ ${amount.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const activeTabLabel = {
    users:     'Usuarios',
    providers: 'Proveedores',
    drivers:   'Conductores',
    referrals: 'Referidos',
  }[activeSubTab];

  // ── Sub-tab config ────────────────────────────────────────────────────────
  const subTabs = [
    { id: 'users',     label: 'Usuarios',     icon: Users    },
    { id: 'providers', label: 'Proveedores',  icon: Package  },
    { id: 'drivers',   label: 'Conductores',  icon: Truck    },
    { id: 'referrals', label: 'Referidos',    icon: UserPlus },
  ];

  return (
    <div className="space-y-4">

      {/* ── Totals Summary ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

        {/* Combined total (Users + Providers) */}
        <div className="sm:col-span-1 bg-gradient-to-br from-[#B1336E] to-[#8f2558] rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-montserrat font-semibold uppercase tracking-wider opacity-80">
              Total del día
            </span>
            <div className="bg-white/20 rounded-lg p-1.5">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-montserrat font-bold leading-tight">
            {formatSoles(totals.combined)}
          </p>
          <p className="text-xs opacity-70 font-montserrat mt-1">
            Usuarios + Proveedores
          </p>
          {/* Breakdown pills */}
          <div className="flex gap-2 mt-3">
            <span className="bg-white/20 text-white text-xs font-montserrat font-semibold px-2 py-0.5 rounded-full">
              U: {formatSoles(totals.users)}
            </span>
            <span className="bg-white/20 text-white text-xs font-montserrat font-semibold px-2 py-0.5 rounded-full">
              P: {formatSoles(totals.providers)}
            </span>
          </div>
        </div>

        {/* Active sub-tab total */}
        <div className="sm:col-span-2 bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-4">
          <div className="bg-[#B1336E]/10 rounded-xl p-3 flex-shrink-0">
            <DollarSign className="w-6 h-6 text-[#B1336E]" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-montserrat font-semibold text-slate-500 uppercase tracking-wider mb-0.5">
              Debe total — {activeTabLabel}
            </p>
            <p className="text-2xl font-montserrat font-bold text-slate-800 leading-tight">
              {formatSoles(totals.activeTab)}
            </p>
            <p className="text-xs text-slate-400 font-montserrat mt-0.5">
              Suma de la columna "Debe" en la pestaña activa
            </p>
          </div>
        </div>
      </div>

      {/* ── Sub-tabs + Refresh ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg font-montserrat font-semibold transition-all
                  ${activeSubTab === tab.id
                    ? 'bg-brand-primary text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all font-montserrat disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* ── Tables ─────────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-slate-600">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="font-montserrat">Cargando datos...</span>
          </div>
        </div>
      ) : (
        <>
          {activeSubTab === 'users'     && <PaymentsTable data={usersData}     type="user"     onUpdate={handleUpdatePayment} />}
          {activeSubTab === 'providers' && <PaymentsTable data={providersData} type="provider" onUpdate={handleUpdatePayment} />}
          {activeSubTab === 'drivers'   && <PaymentsTable data={driversData}   type="driver"   onUpdate={handleUpdatePayment} />}
          {activeSubTab === 'referrals' && <PaymentsTable data={referralsData} type="referido" onUpdate={handleUpdatePayment} />}
        </>
      )}
    </div>
  );
}

export default PaymentsAccounting;
